import {
  DatePicker,
  type FieldValues,
  type FormItemWrapperProps,
  NumberInput,
  PhoneInput,
  Select,
  TextArea,
  TextInput,
  type UseControllerProps,
  type UseFormReturn,
} from '@faceup/form'
import { Typography } from '@faceup/ui-base'
import { FormItemType, SHORT_STRING_MAX_LENGTH } from '@faceup/utils'
import { type AnyObject, type ArraySchema, yup } from '@faceup/validation'
import type { AFForm_itemFragment } from '../../__generated__/graphql'
import { AFFileFormInput } from '../components/AFFileFormInput'
import { AFMultiSelectFormInput } from '../components/AFMultiSelectFormInput'
import { AFScaleFormInput } from '../components/AFScaleFormInput'

export type AFFormCommonProps = UseControllerProps<FieldValues> &
  Omit<FormItemWrapperProps, 'value' | 'defaultValue' | 'placeholder'> & {
    placeholder?: string
    loading?: boolean
  }

export const useAFFormItem = () => {
  const getFormInputValueSchema = (item: AFForm_itemFragment) => {
    const itemType = item.type as FormItemType
    switch (itemType) {
      case FormItemType.SenderName:
        return yup.object({
          type: yup.string().oneOf([FormItemType.SenderName]).required(),
          value: yup.string().max(SHORT_STRING_MAX_LENGTH).required(),
        })
      case FormItemType.Classroom:
        return yup.object({
          type: yup.string().oneOf([FormItemType.Classroom]).required(),
          value: yup.string().max(SHORT_STRING_MAX_LENGTH).required(),
        })
      case FormItemType.SimpleText: {
        const simpleTextSchema = yup.string().max(SHORT_STRING_MAX_LENGTH)
        return yup.object({
          type: yup.string().oneOf([FormItemType.SimpleText]).required(),
          value: item.isRequired ? simpleTextSchema.required() : simpleTextSchema,
        })
      }
      case FormItemType.MoreInformation: {
        return yup.object({
          type: yup.string().oneOf([FormItemType.MoreInformation]).required(),
          value: item.isRequired
            ? yup
                .string()
                .when('$hasMoreInformationVoiceRecording', ([hasVoice], schema) =>
                  hasVoice ? schema : schema.required()
                )
            : yup.string(),
        })
      }
      case FormItemType.MultilineText: {
        if (item.__typename !== 'AF_FormItemMultilineText') {
          throw new Error(`Form item type ${item.type} is missing maxLength property`)
        }
        let multilineTextSchema = yup.string()
        if (item.maxLength) {
          multilineTextSchema = multilineTextSchema.max(item.maxLength)
        }

        return yup.object({
          type: yup.string().oneOf([FormItemType.MultilineText]).required(),
          value: item.isRequired ? multilineTextSchema.required() : multilineTextSchema,
        })
      }
      case FormItemType.Email: {
        const emailSchema = yup.string().email()
        return yup.object({
          type: yup.string().oneOf([FormItemType.Email]).required(),
          value: item.isRequired ? emailSchema.required() : emailSchema,
        })
      }
      case FormItemType.PhoneNumber: {
        const phoneSchema = yup.string().phone()
        return yup.object({
          type: yup.string().oneOf([FormItemType.PhoneNumber]).required(),
          value: item.isRequired ? phoneSchema.required() : phoneSchema,
        })
      }
      case FormItemType.Number: {
        let numberSchema = yup.number().positive().integer()
        if (item.isRequired) {
          numberSchema = numberSchema.required()
        }
        return yup.object({
          type: yup.string().oneOf([FormItemType.Number]).required(),
          value: numberSchema.transform(value => (value ? value : undefined)),
        })
      }
      case FormItemType.Date: {
        const dateSchema = yup.string()
        return yup.object({
          type: yup.string().oneOf([FormItemType.Date]).required(),
          value: item.isRequired ? dateSchema.required() : dateSchema,
        })
      }
      case FormItemType.Select:
        if (item.__typename !== 'AF_FormItemSelect') {
          throw new Error(`Form item type ${item.type} is missing options`)
        }
        return yup.object({
          type: yup.string().oneOf([FormItemType.Select]).required(),
          value: item.isRequired
            ? item.hasOtherOption
              ? yup.string().trim().required()
              : yup
                  .string()
                  .required()
                  .oneOf(item.options.map(o => o.id))
            : item.hasOtherOption
              ? yup.string().trim()
              : yup.string().oneOf(item.options.map(o => o.id)),
        })
      case FormItemType.Category:
      case FormItemType.OrganizationalUnit:
      case FormItemType.Rating:
      case FormItemType.Scale:
        if (
          item.__typename !== 'AF_FormItemCategory' &&
          item.__typename !== 'AF_FormItemOrganizationalUnit' &&
          item.__typename !== 'AF_FormItemRating' &&
          item.__typename !== 'AF_FormItemScale'
        ) {
          throw new Error(`Form item type ${item.type} is missing options`)
        }
        return yup.object({
          type: yup
            .string()
            .oneOf([
              FormItemType.Category,
              FormItemType.OrganizationalUnit,
              FormItemType.Scale,
              FormItemType.Rating,
            ])
            .required(),
          value: item.isRequired
            ? yup
                .string()
                .required()
                .oneOf(item.options.map(o => o.id))
            : yup.string().oneOf(item.options.map(o => o.id)),
        })
      case FormItemType.MultiSelect: {
        if (item.__typename !== 'AF_FormItemMultiSelect') {
          throw new Error(`Form item type ${item.type} is missing options`)
        }
        const elementSchema = item.hasOtherOption
          ? yup
              .string()
              .required() // allow free-text
          : yup
              .string()
              .oneOf(item.options.map(o => o.id))
              .required()

        const minResponses = (item.minResponses ?? 0) - (item.hasOtherOption ? 1 : 0)
        const maxResponses = item.maxResponses ?? Number.POSITIVE_INFINITY
        return yup.object({
          type: yup.string().oneOf([FormItemType.MultiSelect]).required(),
          value: item.isRequired
            ? yup.array().of(elementSchema).required().min(minResponses).max(maxResponses)
            : yup.array().of(elementSchema).min(minResponses).max(maxResponses),
        })
      }
      case FormItemType.File: {
        const fileSchema = yup.array().of(yup.mixed().file())
        return yup.object({
          type: yup.string().oneOf([FormItemType.File]).required(),
          value: item.isRequired ? fileSchema.required() : fileSchema,
        })
      }
      case FormItemType.Matrix: {
        if (item.__typename !== 'AF_FormItemMatrix') {
          throw new Error(`Form item type ${item.type} is missing options or questions`)
        }
        const matrixSchema = yup.object(
          item.questions.reduce<
            Record<string, ArraySchema<string[] | undefined, AnyObject, '', ''>>
          >(
            (acc, question) => ({
              ...acc,
              [question.id]: item.isRequired
                ? yup.array().of(yup.string().required()).min(1).required()
                : yup.array().of(yup.string().required()),
            }),
            {}
          )
        )
        return yup.object({
          type: yup.string().oneOf([FormItemType.Matrix]).required(),
          value: item.isRequired ? matrixSchema.required() : matrixSchema,
        })
      }
      default: {
        const exhaustiveCheck: never = itemType
        throw new Error(`Unhandled item type case: ${exhaustiveCheck}`)
      }
    }
  }

  const renderFormInput = (form: UseFormReturn, item: AFForm_itemFragment) => {
    const commonProps: AFFormCommonProps = {
      name: `${item.id}.value`,
      label: item.labelTranslation.translation,
      hint: item.hintTranslation?.translation,
      disabled: false,
      withAsterisk: item.isRequired,
      control: form.control,
    }

    const itemType = item.type as FormItemType
    switch (itemType) {
      case FormItemType.SenderName:
      case FormItemType.Classroom:
        return <TextInput {...commonProps} />
      case FormItemType.SimpleText:
        return <TextInput {...commonProps} />
      case FormItemType.Email:
        return <TextInput {...commonProps} type='email' />
      case FormItemType.MultilineText:
        return <TextArea {...commonProps} rows={4} />
      case FormItemType.MoreInformation:
        return <TextArea {...commonProps} rows={4} />
      case FormItemType.PhoneNumber:
        return <PhoneInput {...commonProps} />
      case FormItemType.Number:
        return <NumberInput {...commonProps} />
      case FormItemType.Date:
        return (
          <DatePicker
            {...commonProps}
            onChange={date => form.setValue(`${item.id}.value`, date?.toISOString() ?? '')}
          />
        )
      case FormItemType.Select:
      case FormItemType.Category:
      case FormItemType.OrganizationalUnit:
        if (
          item.__typename !== 'AF_FormItemSelect' &&
          item.__typename !== 'AF_FormItemCategory' &&
          item.__typename !== 'AF_FormItemOrganizationalUnit'
        ) {
          throw new Error(`Form item type ${item.type} is missing options`)
        }
        return (
          <Select
            {...commonProps}
            options={
              item.options.map(opt => ({
                label: opt.translation.translation,
                value: opt.id,
              })) ?? []
            }
          />
        )
      case FormItemType.MultiSelect:
        if (item.__typename !== 'AF_FormItemMultiSelect') {
          throw new Error(`Form item type ${item.type} is missing options`)
        }
        return (
          <AFMultiSelectFormInput
            {...commonProps}
            maxResponses={item.maxResponses ?? null}
            options={
              item.options.map(opt => ({
                label: opt.translation.translation,
                value: opt.id,
              })) ?? []
            }
          />
        )
      case FormItemType.Scale:
        if (item.__typename !== 'AF_FormItemScale') {
          throw new Error(`Form item type ${item.type} is missing scale labels`)
        }
        return <AFScaleFormInput {...commonProps} item={item} />
      case FormItemType.File:
        if (item.__typename !== 'AF_FormItemFile') {
          throw new Error(`Form item type ${item.type} is missing file fields`)
        }
        return <AFFileFormInput {...commonProps} />
      case FormItemType.Matrix:
      case FormItemType.Rating:
        // These form item types are not supported in the preview
        return (
          <Typography.Text type='secondary'>
            Preview not available for form item type: {item.type}
          </Typography.Text>
        )
      default: {
        const exhaustiveCheck: never = itemType
        throw new Error(`Unhandled item type case: ${exhaustiveCheck}`)
      }
    }
  }

  return {
    getFormInputValueSchema,
    renderFormInput,
  }
}
